/*
 * THIS IMPORT COMES FIRST, AND THE ORDER IS THE WHOLE POINT.
 *
 * The product was renamed and every saved setting is filed under the old name.
 * lib/keys carries them across as it loads, which has to happen before the
 * store module is evaluated: the store reads its remembered defaults the
 * moment it is created, and a migration that runs after that has migrated
 * nothing anybody is going to look at. Imports run in the order they are
 * written, so this line is load bearing. Do not sort it.
 */
import './lib/keys'

import { createRoot } from 'react-dom/client'
import App from './App'
import { useEditor } from './store/useEditor'
import {
  DEFAULT_THEME,
  storedTheme,
  themeFromSearch,
  themeVars,
  THEME_KEY,
  type Theme,
} from './lib/theme'

function applyTheme() {
  let chosen: Partial<Theme> | null = null
  try {
    const asked = themeFromSearch(window.location.search)
    if (asked === 'reset') {
      localStorage.removeItem(THEME_KEY)
    } else if (asked) {
      chosen = asked
      localStorage.setItem(THEME_KEY, JSON.stringify(asked))
    } else {
      chosen = storedTheme((k) => localStorage.getItem(k))
    }
  } catch {
    // A blocked localStorage is not a reason to render the wrong color or
    // no app at all: the default palette is always a correct answer.
  }
  if (!chosen) return
  const vars = themeVars({ ...DEFAULT_THEME, ...chosen })
  for (const [k, v] of Object.entries(vars)) {
    document.documentElement.style.setProperty(k, v)
  }
}

/**
 * Debug hook. Lets a script drive the editor from the console or from a test:
 *   lightsos.getState().setScene({ night: 0.9 })
 * Harmless in production, and the fastest way to reproduce a design exactly.
 */
;(window as unknown as { lightsos: typeof useEditor }).lightsos = useEditor

/**
 * Deliberately no StrictMode. It double invokes effects in development, which
 * would build and tear down the WebGL context twice on every hot reload.
 */
/*
 * THE PALETTE, BEFORE ANYTHING RENDERS.
 *
 * Applied here rather than in a component so there is no flash of the old
 * color: the custom properties are on the root element before React draws
 * its first frame. ?accent=1b4332 sets it, ?accent=reset clears it, and it is
 * remembered so a reload keeps whatever you were looking at.
 *
 * It sets the accent and the surface green. It cannot touch amber or rose,
 * because those mean unfinished work and money owed on every screen and a
 * theme that could change them could make the app lie.
 */
applyTheme()

createRoot(document.getElementById('root')!).render(<App />)
